// ========== 1. 类型定义 ==========
// types/drag.ts
export interface DragPosition {
  left: number;
  top: number;
}

export interface DragOptions {
  handle?: string; // 拖动手柄选择器
  boundary?: boolean; // 是否限制在屏幕内,默认 true
  onDragEnd?: (position: DragPosition) => void; // 拖动结束回调
}

// 扩展 HTMLElement 类型
interface DragHTMLElement extends HTMLElement {
  _dragCleanup?: () => void;
}

// ========== 2. 指令实现 ==========
// directives/drag.ts
import type { Directive, DirectiveBinding } from "vue";

export const vDragPopover: Directive<DragHTMLElement, DragOptions> = {
  mounted(el: DragHTMLElement, binding: DirectiveBinding<DragOptions>) {
    const options = binding.value || {};

    // 获取拖动手柄元素
    const dragHandle = options.handle
      ? el.querySelector<HTMLElement>(options.handle)
      : el;

    if (!dragHandle) {
      console.warn("[v-drag] 拖动手柄元素未找到");
      return;
    }

    // 设置样式
    el.style.position = "fixed";
    dragHandle.style.cursor = "move";

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let initialLeft = 0;
    let initialTop = 0;

    const onMouseDown = (e: MouseEvent) => {
      // 如果点击的是不需要拖动的元素,则不触发拖动
      const target = e.target as HTMLElement;
      if (target.closest(".no-drag")) {
        return;
      }

      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;

      const rect = el.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;

      el.style.userSelect = "none";
      el.style.transition = "none";

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      let newLeft = initialLeft + deltaX;
      let newTop = initialTop + deltaY;

      // 边界限制(默认开启)
      if (options.boundary !== false) {
        const rect = el.getBoundingClientRect();
        const maxLeft = window.innerWidth - rect.width;
        const maxTop = window.innerHeight - rect.height;

        newLeft = Math.max(0, Math.min(newLeft, maxLeft));
        newTop = Math.max(0, Math.min(newTop, maxTop));
      }

      el.style.left = `${newLeft}px`;
      el.style.top = `${newTop}px`;
      el.style.right = "auto";
      el.style.bottom = "auto";
    };

    const onMouseUp = () => {
      if (!isDragging) return;

      isDragging = false;
      el.style.userSelect = "";

      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);

      // 触发拖动结束回调
      if (options.onDragEnd) {
        const rect = el.getBoundingClientRect();
        options.onDragEnd({
          left: rect.left,
          top: rect.top
        });
      }
    };

    dragHandle.addEventListener("mousedown", onMouseDown);

    // 保存清理函数
    el._dragCleanup = () => {
      dragHandle.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  },

  unmounted(el: DragHTMLElement) {
    if (el._dragCleanup) {
      el._dragCleanup();
    }
  }
};
